import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Policy, PolicyType } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import LoginPoliciesEmpty from "./Empty";
import { GET_POLICIES } from "./gql";
import LoginPolicyList from "./List";
import LoginPolicyPageLayout from "./PageLayout";
import LoginPolicySortHeader from "./SortHeader";
import { PoliciesQueryResponse } from "./types";

const LoginPolicies = () => {
  const { accountName } = useTypedContext(AccountContext);

  const { error, loading, data } = useQuery<PoliciesQueryResponse>(GET_POLICIES);

  useTitle(`Organization Settings · Login Policies List · ${accountName}`);

  const [loginPolicies, takenPolicyNames] = useMemo(() => {
    const policies = data?.policies ?? [];
    return policies.reduce<[Policy[], string[]]>(
      ([loginPolicies, policyNames], policy): [Policy[], string[]] => {
        if (policy.type === PolicyType.Login) {
          loginPolicies.push(policy);
        }
        policyNames.push(policy.name.toLowerCase());
        return [loginPolicies, policyNames];
      },
      [[], []]
    );
  }, [data?.policies]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.policies) {
    return <PageLoading />;
  }

  if (!data?.policies) {
    return <NotFoundPage />;
  }

  return (
    <LoginPolicyPageLayout takenPolicyNames={takenPolicyNames}>
      {loginPolicies.length === 0 && <LoginPoliciesEmpty />}
      {loginPolicies.length > 0 && (
        <>
          <LoginPolicySortHeader />
          <LoginPolicyList loginPolicies={loginPolicies} />
        </>
      )}
    </LoginPolicyPageLayout>
  );
};

export default LoginPolicies;
